/**
 * Hired Equipment adapters over the Module A/B session APIs.
 *
 * POs come from `purchaseOrderService`; vendor rates from `vendorService`.
 * Tasks sit on each PurchaseOrderLineItem (`taskId` / `task`). Prefill copies
 * that per-line task; HE-F13 filters the picker to the unique tasks on the PO.
 */

import type { PurchaseOrder } from './purchaseOrderService';
import { purchaseOrderService } from './purchaseOrderService';
import type { VendorResource } from './vendorService';
import { vendorService } from './vendorService';
import type {
  HiredEquipmentPoResource,
  HiredEquipmentPurchaseOrder,
  TaskOption,
} from '../features/hired-equipment/types';
import { catalogueAttributes } from '../features/hired-equipment/utils/masterResources';
import { mapPurchaseOrderToPoResources } from '../features/hired-equipment/utils/poPrefill';
import {
  mergeTaskOptions,
  uniqueTasksFromPoResources,
  uniqueTasksFromPurchaseOrder,
} from '../features/hired-equipment/utils/poTaskFilter';

export type { HiredEquipmentPoResource, HiredEquipmentPurchaseOrder };

export interface HiredEquipmentPoPrefillResult {
  resources: HiredEquipmentPoResource[];
  tasks: TaskOption[];
  /**
   * The PO's own number/description/vendor, straight from `getPurchaseOrderById` — unlike
   * `getActivePosForVendor`, this is fetched by id, not filtered to ACTIVE POs for a vendor. A
   * grid line (or the anchor) can carry a PO that's no longer ACTIVE (e.g. now fully invoiced),
   * and that PO would otherwise never surface a description anywhere in the UI.
   */
  po: { id: number; poNumber: string; vendorId: number | null; description: string | null };
}

const poCache = new Map<number, HiredEquipmentPoPrefillResult>();

function catalogueMaps(resources: VendorResource[], serviceAreaId: number) {
  const rateByResourceId = new Map<number, number | null>();
  const vehicleTypeByResourceId = new Map<number, string | null>();
  const overtimeEligibleByResourceId = new Map<number, boolean>();
  const unitByResourceId = new Map<number, string | null>();
  for (const resource of resources) {
    const attrs = catalogueAttributes(resource, resources, serviceAreaId);
    rateByResourceId.set(resource.id, attrs.rate);
    overtimeEligibleByResourceId.set(resource.id, attrs.overtimeEligible);
    if (attrs.vehicleType) vehicleTypeByResourceId.set(resource.id, attrs.vehicleType);
    if (attrs.unit) unitByResourceId.set(resource.id, attrs.unit);
  }
  return { rateByResourceId, vehicleTypeByResourceId, overtimeEligibleByResourceId, unitByResourceId };
}

async function getActivePosForVendor(
  vendorId: number,
  serviceAreaId?: number | null,
): Promise<HiredEquipmentPurchaseOrder[]> {
  const rows = await purchaseOrderService.getAllPurchaseOrders({
    vendorIds: [vendorId],
    statuses: ['ACTIVE'],
    ...(serviceAreaId != null ? { serviceAreaIds: [serviceAreaId] } : {}),
  });
  return rows
    .filter((po): po is PurchaseOrder & { vendorId: number } => po.vendorId != null)
    .map((po) => ({
      id: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId,
      status: po.status,
      description: po.description ?? null,
    }));
}

async function loadPo(poId: number): Promise<HiredEquipmentPoPrefillResult> {
  const cached = poCache.get(poId);
  if (cached) return cached;

  const po = await purchaseOrderService.getPurchaseOrderById(poId);
  let rateByResourceId = new Map<number, number | null>();
  let vehicleTypeByResourceId = new Map<number, string | null>();
  let overtimeByResourceId = new Map<number, boolean>();
  let unitByResourceId = new Map<number, string | null>();
  if (po.vendorId != null) {
    try {
      const vendor = await vendorService.getVendorById(po.vendorId);
      const maps = catalogueMaps(vendor.resources ?? [], po.serviceAreaId);
      rateByResourceId = maps.rateByResourceId;
      vehicleTypeByResourceId = maps.vehicleTypeByResourceId;
      overtimeByResourceId = maps.overtimeEligibleByResourceId;
      unitByResourceId = maps.unitByResourceId;
    } catch (error) {
      console.error('Failed to load vendor rates for purchase order', poId, error);
    }
  }

  const resources = mapPurchaseOrderToPoResources(
    po,
    rateByResourceId,
    vehicleTypeByResourceId,
    overtimeByResourceId,
    unitByResourceId,
  );
  const result: HiredEquipmentPoPrefillResult = {
    resources,
    tasks: mergeTaskOptions(
      uniqueTasksFromPurchaseOrder(po.lineItems ?? []),
      uniqueTasksFromPoResources(resources),
    ),
    po: {
      id: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId ?? null,
      description: po.description ?? null,
    },
  };
  poCache.set(poId, result);
  return result;
}

async function getPoResources(poId: number): Promise<HiredEquipmentPoResource[]> {
  const { resources } = await loadPo(poId);
  return resources;
}

async function getTasksForPo(poId: number): Promise<TaskOption[]> {
  const { tasks } = await loadPo(poId);
  return tasks;
}

export const hiredEquipmentPoService = {
  getActivePosForVendor,
  getPoResources,
  getTasksForPo,
  loadPo,
};
